import React, { useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { API_URL } from '../../../constants/api';
import { decodeToken, getToken } from '../../../utils/TokenUtils';
import { exportElementToPDF } from '../../../utils/ktcUtils';
import { notification } from 'antd';
import { Link } from 'react-router-dom';

const BalanceSheet = ({ report, link, value }) => {
  const reportRef = useRef(null);
  const token = getToken();
  const companyName = decodeToken(token, 'companyName');
  const companyBanner = decodeToken(token, 'banner');

  /* ---------------- helpers ---------------- */
  const parseAmount = (v) =>
    typeof v === 'string'
      ? parseFloat(v.replace(/[^0-9.-]/g, ''))
      : Number(v || 0);

  const formatAmount = (v) =>
    `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  /* ---------------- BUILD BALANCE SHEET ---------------- */
  const statement = useMemo(() => {
    if (!Array.isArray(report)) return [];

    const buildSection = (accountType) => {
      const sections = {};

      report
        .filter((r) => r.AccountType === accountType)
        .forEach((r) => {
          if (!sections[r.Section]) sections[r.Section] = [];
          sections[r.Section].push({
            accountId: r.account_id,
            mainAccountId: r.main_account_id,
            label: r.Account,
            amount: parseAmount(r.Amount),
            level: 1,
          });
        });

      return Object.entries(sections).map(([section, rows]) => ({
        section,
        rows,
        total: rows.reduce((s, r) => s + r.amount, 0),
      }));
    };

    const assets = buildSection('Asset');
    const liabilities = buildSection('Liability');
    const equity = buildSection('Equity');

    const totalAssets = assets.reduce((s, x) => s + x.total, 0);
    const totalLiabilities = liabilities.reduce((s, x) => s + x.total, 0);
    const totalEquity = equity.reduce((s, x) => s + x.total, 0);

    return {
      assets,
      liabilities,
      equity,
      totals: {
        assets: totalAssets,
        liabilities: totalLiabilities,
        equity: totalEquity,
      },
    };
  }, [report]);

  console.log('statement:', statement);
  /* ---------------- EXPORT HELPERS ---------------- */
  const flattenForExport = () => {
    const rows = [];

    const pushSections = (title, sections, total) => {
      rows.push({ Account: title, Amount: '' });

      sections.forEach((sec) => {
        rows.push({ Account: sec.section, Amount: '' });

        sec.rows.forEach((r) =>
          rows.push({
            Account: '    ' + r.label,
            Amount: r.amount,
          })
        );

        rows.push({
          Account: `Total ${sec.section}`,
          Amount: sec.total,
        });

        rows.push({ Account: '', Amount: '' });
      });

      rows.push({
        Account: `TOTAL ${title.toUpperCase()}`,
        Amount: total,
      });

      rows.push({ Account: '', Amount: '' });
    };

    pushSections('Assets', statement.assets, statement.totals.assets);
    pushSections(
      'Liabilities',
      statement.liabilities,
      statement.totals.liabilities
    );
    pushSections('Equity', statement.equity, statement.totals.equity);

    return rows;
  };

  /* ---------------- EXPORT ACTIONS ---------------- */
  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(flattenForExport());
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Balance Sheet');
    XLSX.writeFile(wb, `Balance Sheet as of ${value.to}.xlsx`);
  };

  const exportCSV = () => {
    const rows = flattenForExport();
    const csv =
      'Account,Amount\n' +
      rows.map((r) => `"${r.Account}",${r.Amount}`).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Balance Sheet as of ${value.to}.csv`;
    link.click();
  };

  const exportPDF = async () => {
    try {
      await exportElementToPDF({
        element: reportRef.current,
        fileName: `Balance Sheet as of ${value.to}.pdf`,
        orientation: 'p',
      });
    } catch (err) {
      notification.error({
        message: 'PDF Export Failed',
        description: err.message,
      });
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <>
      <div style={{ marginTop: 20, textAlign: 'center' }}>
        <button className="btn btn-success me-2" onClick={exportExcel}>
          Export Excel
        </button>
        <button className="btn btn-secondary me-2" onClick={exportCSV}>
          Export CSV
        </button>
        <button className="btn btn-danger" onClick={exportPDF}>
          Export PDF
        </button>
      </div>

      <br />

      <div style={styles.container} ref={reportRef}>
        <div style={styles.headerSection}>
          <img
            src={`${API_URL}/${companyBanner}`}
            alt="Banner"
            style={styles.logo}
          />
          <h2>{companyName}</h2>
          <h3 style={{ fontWeight: 'bold' }}>
            Balance Sheet as of <br />
            <small>{value.to}</small>
          </h3>
        </div>

        <table style={styles.table}>
          <tbody>
            {/* ASSETS */}
            <tr>
              <td style={styles.mainAccount}>ASSETS</td>
              <td style={styles.amount}></td>
            </tr>

            {statement.assets.map((sec, i) => (
              <React.Fragment key={i}>
                <tr>
                  <td style={styles.section}>{sec.section}</td>
                  <td></td>
                </tr>

                {sec.rows.map((r, idx) => (
                  <tr key={idx}>
                    <td style={{ paddingLeft: 24 }}>{r.label}</td>
                    <td style={styles.amount}>
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`/form-report/transactions-list?account_type=Asset&main_account_id=${r.mainAccountId}&account_id=${r.accountId}&from=${value.from}&to=${value.to}`}
                      >
                        {formatAmount(r.amount)}
                      </a>
                    </td>
                  </tr>
                ))}

                <tr>
                  <td style={styles.totalLabel}>Total {sec.section}</td>
                  <td style={styles.totalAmount}>{formatAmount(sec.total)}</td>
                </tr>
              </React.Fragment>
            ))}

            <tr>
              <td style={styles.grandTotal}>TOTAL ASSETS</td>
              <td style={styles.grandTotalAmount}>
                {formatAmount(statement.totals.assets)}
              </td>
            </tr>

            {/* LIABILITIES + EQUITY */}
            <tr>
              <td style={styles.mainAccount}>LIABILITIES & EQUITY</td>
              <td></td>
            </tr>

            {[...statement.liabilities, ...statement.equity].map((sec, i) => (
              <React.Fragment key={i}>
                <tr>
                  <td style={styles.section}>{sec.section}</td>
                  <td></td>
                </tr>

                {sec.rows.map((r, idx) => (
                  <tr key={idx}>
                    <td style={{ paddingLeft: 24 }}>{r.label}</td>
                    <td style={styles.amount}>
                      {' '}
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`/form-report/transactions-list?account_type=Liability&main_account_id=${r.mainAccountId}&account_id=${r.accountId}&from=${value.from}&to=${value.to}`}
                      >
                        {formatAmount(r.amount)}
                      </a>
                    </td>
                  </tr>
                ))}

                <tr>
                  <td style={styles.totalLabel}>Total {sec.section}</td>
                  <td style={styles.totalAmount}>{formatAmount(sec.total)}</td>
                </tr>
              </React.Fragment>
            ))}

            <tr>
              <td style={styles.grandTotal}>TOTAL LIABILITIES & EQUITY</td>
              <td style={styles.grandTotalAmount}>
                {formatAmount(
                  statement.totals.liabilities + statement.totals.equity
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <br />
      <br />
    </>
  );
};

/* ---------------- STYLES ---------------- */
const styles = {
  container: { width: '40%', margin: 'auto' },
  headerSection: { textAlign: 'center', marginBottom: 20 },
  logo: { width: '100%', height: 150, objectFit: 'cover' },
  table: { width: '100%' },
  mainAccount: { fontWeight: 'bold', paddingTop: 10 },
  section: { fontWeight: 'bold', paddingTop: 5 },
  amount: { textAlign: 'right' },
  totalLabel: { fontWeight: 'bold', paddingTop: 5 },
  totalAmount: { fontWeight: 'bold', textAlign: 'right' },
  grandTotal: {
    fontWeight: 'bold',
    borderTop: '2px solid black',
    paddingTop: 8,
  },
  grandTotalAmount: {
    fontWeight: 'bold',
    textAlign: 'right',
    borderTop: '2px solid black',
  },
};

export default BalanceSheet;
